import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import attendanceService from "../../services/attendanceService";
import { getAppSocket } from "../../hooks/useSocket";
import { formatTimeRange12h } from "../../utils/timetableTimeSlots";

const EMPTY_ROSTER = { attendance: [], remaining: [], onLeave: [] };

const safeArray = (value) => (Array.isArray(value) ? value : []);

const getStudentName = (row) =>
  row?.fullName || row?.user?.fullName || row?.name || row?.studentName || "—";

const getStudentRoll = (row) =>
  row?.rollNumber || row?.studentLoginID || row?.rollNo || "—";

const getStudentMethod = (row) => row?.attendance?.method || "—";

const getMarkedAt = (row) => {
  const markedAt = row?.attendance?.markedAt;
  if (!markedAt) return "—";
  try {
    const d = new Date(markedAt);
    return Number.isFinite(d.getTime()) ? d.toLocaleString() : "—";
  } catch {
    return "—";
  }
};

/**
 * READ-ONLY live roster dialog for a RUNNING lecture card.
 *
 * It owns its own data lifecycle: on open it fetches the roster for the card's slot
 * context (classId + date + subjectId + timeTableSlotId, NO sessionId — the backend
 * resolves the live session) and keeps it fresh with the SAME mechanism the page list
 * uses: a 5s poll loop PLUS a 200ms-debounced refresh on the `attendance_marked` socket
 * event. Everything is guarded per the no-crash steering so it degrades to an error /
 * empty state and never white-screens.
 */
const LiveRosterDialog = ({ schedule, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [roster, setRoster] = useState(EMPTY_ROSTER);

  // Monotonic guard so a late response after close / re-open cannot overwrite state.
  const seqRef = useRef(0);

  const classId = schedule?.classId;
  const subjectId = schedule?.subjectId;
  const timeTableSlotId = schedule?.timeTableSlotId;
  const date = schedule?.date;

  const fetchRoster = useCallback(async () => {
    if (!classId || !date) return;
    const seq = seqRef.current;
    try {
      const res = await attendanceService.getRoster(classId, {
        date,
        subjectId: subjectId || undefined,
        timeTableSlotId: timeTableSlotId || undefined,
        page: 1,
        limit: 500,
      });
      if (seq !== seqRef.current) return; // stale response (dialog closed / changed)
      const data = res?.data?.data?.data || {};
      setRoster({
        attendance: safeArray(data.attendance),
        remaining: safeArray(data.remaining),
        onLeave: safeArray(data.onLeave),
      });
      setError("");
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(e?.response?.data?.message || e?.message || "Failed to load roster");
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [classId, subjectId, timeTableSlotId, date]);

  const fetchRosterRef = useRef(fetchRoster);
  fetchRosterRef.current = fetchRoster;

  useEffect(() => {
    if (!open || !schedule) return undefined;

    // New lifecycle: bump the sequence so any in-flight request from a previous
    // open is ignored, reset view state and do an immediate fetch.
    seqRef.current += 1;
    setLoading(true);
    setError("");
    setRoster(EMPTY_ROSTER);
    void fetchRosterRef.current?.();

    let disposed = false;
    let debounceTimer = null;

    const pollTimer = setInterval(() => {
      if (disposed) return;
      void fetchRosterRef.current?.();
    }, 5000);

    const socket = getAppSocket();

    const cleanup = () => {
      disposed = true;
      // Invalidate any in-flight response so it cannot set state after cleanup.
      seqRef.current += 1;
      clearInterval(pollTimer);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    };

    if (!socket) {
      return cleanup;
    }

    const attendanceMarkedHandler = () => {
      if (disposed || debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        if (!disposed) void fetchRosterRef.current?.();
      }, 200);
    };

    if (typeof socket.on === "function") {
      socket.on("attendance_marked", attendanceMarkedHandler);
    }

    return () => {
      cleanup();
      if (typeof socket.off === "function") {
        socket.off("attendance_marked", attendanceMarkedHandler);
      }
    };
    // Keyed on the dialog being open for a specific card's slot context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId, subjectId, timeTableSlotId, date]);

  const presentRows = safeArray(roster?.attendance);
  const absentRows = safeArray(roster?.remaining);
  const leaveRows = safeArray(roster?.onLeave);

  const presentCount = presentRows.length;
  const absentCount = absentRows.length;
  const onLeaveCount = leaveRows.length;
  const totalCount = presentCount + absentCount + onLeaveCount;

  const hasData = totalCount > 0;
  const showInitialSpinner = loading && !hasData && !error;
  const showEmpty = !loading && !error && !hasData;

  const classLabel = `${schedule?.className || (classId ? `Class ${classId}` : "")}${
    schedule?.classSection ? ` - ${schedule.classSection}` : ""
  }`.trim();

  const renderRow = (row, idx, statusLabel, statusColor) => (
    <TableRow key={`${statusLabel}-${row?.id ?? idx}`}>
      <TableCell>{getStudentName(row)}</TableCell>
      <TableCell>{getStudentRoll(row)}</TableCell>
      <TableCell>
        <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" />
      </TableCell>
      <TableCell>{getStudentMethod(row)}</TableCell>
      <TableCell>{getMarkedAt(row)}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" component="div" fontWeight={600}>
          {schedule?.subjectName || "Live attendance"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[classLabel, formatTimeRange12h(schedule?.startTime, schedule?.endTime)]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
        {schedule?.teacherName ? (
          <Typography variant="body2" color="text.secondary">
            Teacher: {schedule.teacherName}
          </Typography>
        ) : null}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Chip label={`Total: ${totalCount}`} size="small" variant="outlined" />
          <Chip label={`Present: ${presentCount}`} size="small" color="success" variant="outlined" />
          <Chip label={`Absent: ${absentCount}`} size="small" color="error" variant="outlined" />
          <Chip label={`On Leave: ${onLeaveCount}`} size="small" color="warning" variant="outlined" />
          {loading && hasData ? <CircularProgress size={16} sx={{ ml: 1 }} /> : null}
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {showInitialSpinner ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : showEmpty ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No students / roster unavailable
          </Typography>
        ) : hasData ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Roll / Login</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Marked</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {presentRows.map((row, idx) => renderRow(row, idx, "Present", "success"))}
                {absentRows.map((row, idx) => renderRow(row, idx, "Absent", "error"))}
                {leaveRows.map((row, idx) => renderRow(row, idx, "On Leave", "warning"))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LiveRosterDialog;
